import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { findGame } from './games';
import type { GameContext, GameDef, RoundResult } from './games/registry';
import { flushPending, submitScore } from './net/api';
import { loadPlayer, recordBest } from './player';
import { Sfx } from './ui/audio';
import { clear } from './ui/dom';
import { Buzz } from './ui/haptics';
import { renderBoard } from './ui/leaderboard';
import { renderPicker } from './ui/picker';

/**
 * The shell: a hash router over three screens — the picker (#/), a game (#/play/<id>)
 * and its leaderboard (#/board/<id>) — plus the bits every game shares (player, sound,
 * haptics, posting scores). Games live in src/games/<id>/ and know nothing about routing.
 */
const app = document.getElementById('app')!;
const player = loadPlayer();
const sfx = new Sfx();
const buzz = new Buzz();
sfx.enabled = player.sound;
buzz.enabled = player.haptics;

// browsers only allow audio after a gesture; unlock on any tap, and resume if suspended
document.addEventListener('pointerdown', () => sfx.unlock(), { capture: true, passive: true });

let teardown: (() => void) | null = null;
let backHandler: (() => boolean) | null = null;

function go(hash: string): void {
  if (location.hash === hash) route();
  else location.hash = hash;
}

function contextFor(game: GameDef): GameContext {
  return {
    root: app,
    player,
    sfx,
    buzz,
    exit: () => go('#/'),
    showBoard: () => go(`#/board/${game.id}`),
    setBackHandler: (fn) => (backHandler = fn),
    async finish(result: RoundResult) {
      const newBest = result.practice ? false : recordBest(game.id, result.score, result.mistakes, game.lowerIsBetter);
      const board = !result.practice && player.name ? await this.post(result) : null;
      return { board, newBest };
    },
    post: (result: RoundResult) =>
      submitScore({ game: game.id, player: player.id, name: player.name, score: result.score, mistakes: result.mistakes, detail: result.detail }),
  };
}

function route(): void {
  teardown?.();
  teardown = null;
  backHandler = null;
  clear(app);
  const m = /^#\/(play|board)\/([a-z0-9-]+)$/.exec(location.hash);
  const game = m ? findGame(m[2]) : undefined;
  document.body.dataset.screen = m && game ? m[1] : 'picker';
  if (m && game && m[1] === 'play') {
    teardown = game.mount(contextFor(game));
  } else if (m && game) {
    teardown = renderBoard(app, game, player, { back: () => go('#/'), play: () => go(`#/play/${game.id}`) });
  } else {
    teardown = renderPicker(app, player, {
      play: (g) => go(`#/play/${g.id}`),
      board: (g) => go(`#/board/${g.id}`),
      changed: () => {
        sfx.enabled = player.sound;
        buzz.enabled = player.haptics;
      },
    });
  }
  window.scrollTo(0, 0);
}

window.addEventListener('hashchange', route);
route();

// anything that couldn't post while offline
void flushPending();
window.addEventListener('online', () => void flushPending());

// the Android back button / iOS back gesture inside the shells
if (Capacitor.isNativePlatform()) {
  void CapApp.addListener('backButton', () => {
    if (backHandler?.()) return;
    if (location.hash && location.hash !== '#/') go('#/');
    else void CapApp.exitApp();
  });
}
